import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SupportCaseAttachment } from '@app/areas/crm/support-cases/services/support-cases-api.service';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'app-rich-description-editor',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './rich-description-editor.component.html',
  styleUrls: ['./rich-description-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichDescriptionEditorComponent),
      multi: true,
    },
  ],
})
export class RichDescriptionEditorComponent implements ControlValueAccessor, OnChanges {
  @Input() placeholder = 'Write description in Markdown or drag & drop files directly here...';
  @Input() attachments: SupportCaseAttachment[] = [];
  @Input() localFileMap = new Map<string, string>();
  @Input() apiEndpoint = '';
  @Input() caseId = '';
  @Input() disabled = false;

  @Output() fileDrop = new EventEmitter<File>();
  @Output() deleteAttachment = new EventEmitter<string>();

  @ViewChild('editor', { static: true }) editorRef!: ElementRef<HTMLDivElement>;

  isDraggingOver = false;
  private innerValue = '';

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attachments'] || changes['localFileMap'] || changes['apiEndpoint'] || changes['caseId']) {
      this.syncDomFromValue(this.innerValue);
    }
  }

  writeValue(value: string): void {
    this.innerValue = value || '';
    this.syncDomFromValue(this.innerValue);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.editorRef?.nativeElement) {
      this.editorRef.nativeElement.contentEditable = isDisabled ? 'false' : 'true';
    }
  }

  onEditorInput(): void {
    const markdown = this.serializeDomToMarkdown();
    this.innerValue = markdown;
    this.onChange(markdown);
  }

  onEditorBlur(): void {
    this.onTouched();
  }

  onEditorClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const removeBtn = target?.closest('.remove-widget-btn') as HTMLElement | null;
    if (removeBtn) {
      event.stopPropagation();
      event.preventDefault();
      const attachmentId = removeBtn.getAttribute('data-attachment-id');
      if (attachmentId) {
        const widgetEl = removeBtn.closest('.inline-image-widget');
        widgetEl?.remove();
        this.onEditorInput();
        this.deleteAttachment.emit(attachmentId);
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;

    if (event.dataTransfer?.files?.length) {
      const file = event.dataTransfer.files[0];
      this.fileDrop.emit(file);
    }
  }

  private syncDomFromValue(markdown: string): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return;

    // Check if DOM already produces the exact same markdown (prevents cursor jump)
    const currentMarkdown = this.serializeDomToMarkdown();
    if (currentMarkdown === markdown && editor.children.length > 0) {
      return;
    }

    editor.innerHTML = this.convertMarkdownToHtml(markdown);
  }

  private convertMarkdownToHtml(markdown: string): string {
    if (!markdown) return '';

    const lines = markdown.split(/\r?\n/);
    const htmlParts: string[] = [];

    for (const line of lines) {
      if (!line.trim()) {
        htmlParts.push('<div><br></div>');
        continue;
      }

      const lineHtml = this.escapeHtml(line).replace(
        /!\[([^\]]*)]\(attachment:([a-zA-Z0-9_.-]+)\)/g,
        (_match, alt: string, id: string) => {
          return this.createImageWidgetHtml(id, alt || 'Image');
        }
      );

      htmlParts.push(`<div>${lineHtml}</div>`);
    }

    return htmlParts.join('');
  }

  private createImageWidgetHtml(id: string, alt: string): string {
    const isUploading = id.startsWith('uploading-');
    const foundAtt = this.attachments?.find((a) => a.id === id);
    const filename = foundAtt?.filename || alt || 'Attachment';
    const fileSize = foundAtt?.fileSize
      ? this.formatFileSize(foundAtt.fileSize)
      : (isUploading ? 'Uploading...' : '');

    let imageUrl = '';
    if (this.localFileMap?.has(id)) {
      imageUrl = this.localFileMap.get(id)!;
    } else if (this.caseId && this.apiEndpoint) {
      imageUrl = `${this.apiEndpoint}/supportCases/${this.caseId}/attachments/${id}/file`;
    } else {
      imageUrl = `attachment:${id}`;
    }

    const spinnerHtml = isUploading
      ? `<span class="mdi mdi-loading mdi-spin text-sky-400 text-xs inline-block ml-1.5" title="Uploading..."></span>`
      : '';

    const borderClasses = isUploading
      ? 'border-sky-500/50 bg-sky-950/20 shadow-sky-950/40'
      : 'border-slate-800 bg-slate-900/90';

    return `
      <div contenteditable="false" data-attachment-id="${this.escapeAttribute(id)}" data-filename="${this.escapeAttribute(filename)}" class="inline-image-widget group relative my-3 flex items-center gap-3 rounded-2xl border ${borderClasses} p-3 shadow-md select-none max-w-md">
        <div class="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-slate-800 bg-slate-950">
          <img src="${this.escapeAttribute(imageUrl)}" alt="${this.escapeAttribute(filename)}" class="h-full w-full object-cover ${isUploading ? 'opacity-60 blur-[0.5px]' : ''}" />
          ${isUploading ? `<div class="absolute inset-0 flex items-center justify-center bg-slate-950/60 text-sky-400"><span class="mdi mdi-loading mdi-spin text-base"></span></div>` : ''}
        </div>
        <div class="min-w-0 flex-1 pr-7">
          <p class="truncate text-xs font-medium text-slate-200 flex items-center" title="${this.escapeAttribute(filename)}">
            <span class="truncate">${this.escapeHtml(filename)}</span>
            ${spinnerHtml}
          </p>
          <p class="text-[10px] ${isUploading ? 'text-sky-400 font-medium' : 'text-slate-400'}">${this.escapeHtml(fileSize)}</p>
        </div>
        <button type="button" data-attachment-id="${this.escapeAttribute(id)}" class="remove-widget-btn absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-lg bg-slate-800/80 text-slate-400 hover:bg-rose-500/20 hover:text-rose-400 transition cursor-pointer" title="Remove image">
          <span class="mdi mdi-close text-xs pointer-events-none"></span>
        </button>
      </div>
    `.trim();
  }

  private serializeDomToMarkdown(): string {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return '';

    const processNode = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;

        if (el.classList.contains('inline-image-widget')) {
          const id = el.getAttribute('data-attachment-id') || '';
          const filename = el.getAttribute('data-filename') || 'Image';
          return id ? `![${filename}](attachment:${id})` : '';
        }

        if (el.tagName.toLowerCase() === 'br') {
          return '\n';
        }

        let result = '';
        for (const child of Array.from(el.childNodes)) {
          result += processNode(child);
        }

        const isBlock = ['div', 'p', 'section', 'article'].includes(el.tagName.toLowerCase());
        return isBlock ? `${result}\n` : result;
      }

      return '';
    };

    let fullText = '';
    for (const child of Array.from(editor.childNodes)) {
      fullText += processNode(child);
    }

    return fullText
      .split('\n')
      .map(line => line.trimEnd())
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  private formatFileSize(bytes: number): string {
    if (!bytes || bytes <= 0) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  }

  private escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private escapeAttribute(str: string): string {
    return this.escapeHtml(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}
